import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { DeletionStatus, FileDeletionType } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { DeletionStatusResponse } from '@/api/types/backend-dto-types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface EnqueueDeleteResponse {
  historyId: string;
}

/**
 * Enqueues a doc-deletion job. Returns immediately — the actual deletion runs server-side via
 * BullMQ.
 */
export const useDeleteMatterDocs = (matterId: string, onEnqueued: (docIds: string[]) => void) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const deleteDocs = async (docIds: string[]): Promise<EnqueueDeleteResponse> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.DELETE,
      data: { docIds },
    });

    const response = await fetch(`${APIBaseChronos}/api/matters/${matterId}/docs`, fetchConfig);
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? 'Deleting docs failed');
    }
    return response.json();
  };

  return useMutation({
    mutationFn: (docIds: string[]) => deleteDocs(docIds),
    onSuccess: (data, variables) => {
      toast.success('Deletion started');
      queryClient.setQueryData<DeletionStatusResponse>([QueryEntity.DeletionStatus, { matterId }], {
        // Immediately seed query for instant feedback
        inProgress: {
          id: data.historyId,
          createdAt: new Date().toISOString(),
          type: FileDeletionType.Submitted,
          status: DeletionStatus.Processing,
          failureReason: null,
          totalDocs: variables.length,
          completedDocs: 0,
          completedAt: null,
          createdBy: { id: '', email: '' },
          fileDeletion: [],
        },
        hasPostDeletionRun: false,
      });
      queryClient.invalidateQueries({ queryKey: [QueryEntity.DeletionStatus, { matterId }] });
      onEnqueued(variables);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete documents. Please try again.');
    },
  });
};
